import React, { useCallback, useEffect, useState } from 'react';

import { capitalize } from '@mui/material';
import { t } from 'i18next';

import ErrorIcon from '../../../../../assets/icons/v3/error.svg?url';
import { TRANSLATION_MANAGE_COLLABORATORS } from '../../../../translations/manageCollaborators';
import { GenericObject } from '../../../../types/genericTypes';
import BdsInput from '../../BdsInput/BdsInput';
import BdsTooltip from '../../BdsTooltip/BdsTooltip';
import './StyledTableEditableRowItem.scss';

interface IStyledTableEditableRowItem {
  member?: GenericObject;
  fieldName: string;
  onChangeField: (field: string, value: GenericObject) => void;
}

const StyledTableEditableRowItem = ({ member, fieldName, onChangeField }: IStyledTableEditableRowItem) => {
  const [error, setError] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [onHover, setOnHover] = useState<boolean>(false);

  const fieldValue = member?.[fieldName];
  const isEdited = 'hasEdited';

  const validateFieldValue = useCallback(() => {
    if (!fieldValue) {
      return t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.emptyField);
    }
    if (fieldValue.error) {
      return fieldValue.error;
    }
    return '';
  }, [fieldValue]);

  useEffect(() => {
    const validationError = validateFieldValue();
    setError(validationError);

    if (!editing) {
      setInputValue(fieldValue?.value || '');
    }
  }, [fieldValue, validateFieldValue, editing]);

  const handleOnChangeValueField = (newValue: string) => {
    setEditing(true);
    setInputValue(newValue);
  };

  const handleOnBlur = (newValue: string) => {
    if (!member || newValue === fieldValue?.value) return;

    const updatedMember = {
      ...member,
      [fieldName]: { value: newValue, error: isEdited }
    };
    onChangeField(fieldName, updatedMember);
    setEditing(false);
  };

  const commonClasses = `editable-row flex justify-start ${fieldName}`;

  if (error && error !== isEdited) {
    return (
      <button
        key={`editable-row-key-${fieldName}`}
        className={commonClasses}
        onMouseEnter={() => {
          setOnHover(!onHover);
        }}
        onMouseLeave={() => setOnHover(false)}
      >
        <BdsTooltip message={error} position="bottom-center" showPointer={true} open={onHover}>
          <BdsInput
            prefixIcon={ErrorIcon}
            onChanged={handleOnChangeValueField}
            initValue={inputValue}
            focusedError={true}
            className="w-full"
            hintText={
              inputValue !== ''
                ? inputValue
                : t(TRANSLATION_MANAGE_COLLABORATORS.uploadCollaboratorsSection.subscriptionByFile.table.emptyField)
            }
            onSubmitPressed={handleOnBlur}
            onBlur={handleOnBlur}
          />
        </BdsTooltip>
      </button>
    );
  }

  if (editing || error === isEdited) {
    return (
      <div key={`editable-row-key-${fieldName}`} className={commonClasses}>
        <BdsInput
          onChanged={handleOnChangeValueField}
          initValue={inputValue}
          focusedError={error !== isEdited && error !== ''}
          className="w-full"
          onSubmitPressed={handleOnBlur}
          onBlur={handleOnBlur}
        />
      </div>
    );
  }

  return (
    <div key={`editable-row-key-${fieldName}`} className={`${commonClasses}`}>
      <div className="default-txt">{capitalize(inputValue)}</div>
    </div>
  );
};

export default StyledTableEditableRowItem;
